import {
  FLAGS,
  type FlagJson,
  type MaintenanceBanner as MaintenanceBannerValue,
} from '@zuruflex/types';
import { AlertTriangle, Info, OctagonAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';

/**
 * The `app.maintenanceBanner` flag's value, if it is one worth drawing.
 *
 * The admin validates on save, but what arrives here is whatever the API
 * served — and a build that predates a change to the shape is sent the new
 * one anyway. So the catalogue's own `validate` is asked again rather than
 * a second copy of the rule, and anything it refuses draws nothing: a
 * missing banner is a better failure than a crashed shell.
 */
export function readMaintenanceBanner(value: FlagJson): MaintenanceBannerValue | null {
  if (value === null || FLAGS['app.maintenanceBanner'].validate(value) !== null) return null;
  return value as unknown as MaintenanceBannerValue;
}

/**
 * Only links a browser can follow without surprise: an absolute http(s) URL,
 * or a path on this site. Anything else — `javascript:` above all — is the
 * banner without its link rather than a link that runs something.
 */
function safeHref(href: string | undefined): string | null {
  if (!href) return null;
  if (href.startsWith('/') && !href.startsWith('//')) return href;
  try {
    const url = new URL(href);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null;
  } catch {
    return null;
  }
}

const TONE = {
  info: { className: 'bg-info/10 text-info', Icon: Info },
  warning: { className: 'bg-warning/10 text-warning', Icon: AlertTriangle },
  // The one tone that is a fill rather than a tint: an incident has to read
  // as louder than a notice, not as the same strip in a different hue.
  critical: { className: 'bg-danger-solid text-danger-on', Icon: OctagonAlert },
} as const;

export interface MaintenanceBannerProps {
  /** `useFlag('app.maintenanceBanner')`, as served. Validated here. */
  value: FlagJson;
  className?: string;
}

/**
 * A slim, full-width strip across the top of an app, for planned downtime or
 * an incident. Driven entirely by the `app.maintenanceBanner` flag, so it
 * can be raised from the admin without a deploy — and it takes the flag's
 * value as a prop rather than reading it, because this package does not
 * know about the API and each app mounts its own `FlagsProvider`.
 *
 * In the document flow rather than fixed: it pushes the app down instead of
 * covering the first line of whatever page is open, and it scrolls away
 * with the page so a sticky header still sticks to the top.
 */
export function MaintenanceBanner({ value, className }: MaintenanceBannerProps) {
  const { t } = useTranslation('common');
  const banner = readMaintenanceBanner(value);
  if (!banner) return null;

  const { className: tone, Icon } = TONE[banner.tone];
  const href = safeHref(banner.href);
  const external = href !== null && !href.startsWith('/');

  return (
    <div
      role={banner.tone === 'critical' ? 'alert' : 'status'}
      className={cn('w-full text-sm', tone, className)}
    >
      <div className="flex items-center justify-center gap-2 px-4 py-2 text-center">
        <Icon className="size-4 shrink-0" aria-hidden="true" />
        <p className="min-w-0">
          <span className="font-medium">{banner.message}</span>
          {href ? (
            <>
              {' '}
              <a
                href={href}
                className="font-semibold underline underline-offset-2 hover:no-underline"
                {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {t('action.learnMore')}
              </a>
            </>
          ) : null}
        </p>
      </div>
    </div>
  );
}
